import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { resolveCoverUrl } from '../../services/api';
import AdminTable from '../components/AdminTable';
import AdminModal from '../components/AdminModal';
import styles from './NovelManagement.module.css';
import { PlusIcon, BookIcon, CheckIcon, EditIcon } from '../../components/Icons';

function NovelManagement() {
  const [novels, setNovels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNovel, setEditingNovel] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    translation_prompt: '',
    book_url: '',
    cover_image_url: '',
    upload_by: '',
    is_completed: false,
    is_latest: 0,
    is_tuijian: 0,
    categories: [],
    feature_order: 0,
  });

  useEffect(() => {
    fetchNovels();
    fetchCategories();
    fetchAdminUsers();
  }, []);

  const fetchNovels = async () => {
    try {
      setLoading(true);
      const response = await api.get('/novels/');
      const normalized = Array.isArray(response.data)
        ? response.data.map((novel) => ({
            ...novel,
            cover_image_url: resolveCoverUrl(novel.cover_image_url),
          }))
        : [];
      setNovels(normalized);
    } catch (error) {
      console.error('获取小说列表失败:', error);
      alert('获取小说列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories/');
      setCategories(response.data);
    } catch (error) {
      console.error('获取分类列表失败:', error);
    }
  };

  const fetchAdminUsers = async () => {
    try {
      const response = await api.get('/users/admins');
      setAdminUsers(response.data);
    } catch (error) {
      console.error('获取管理员列表失败:', error);
    }
  };

  const handleCreate = () => {
    setEditingNovel(null);
    setFormData({
      title: '',
      author: '',
      description: '',
      translation_prompt: '',
      book_url: '',
      cover_image_url: '',
      upload_by: '',
      is_completed: false,
      is_latest: 0,
      is_tuijian: 0,
      categories: [],
      feature_order: 0,
    });
    setShowModal(true);
  };

  const handleEdit = (novel) => {
    setEditingNovel(novel);
    setFormData({
      title: novel.title || '',
      author: novel.author || '',
      description: novel.description || '',
      translation_prompt: novel.translation_prompt || '',
      book_url: novel.book_url || '',
      cover_image_url: novel.cover_image_url || '',
      upload_by: novel.upload_by || '',
      is_completed: novel.is_completed || false,
      is_latest: Number.isInteger(novel.is_latest) ? novel.is_latest : 0,
      is_tuijian: Number.isInteger(novel.is_tuijian) ? novel.is_tuijian : 0,
      categories: novel.categories?.map(cat => cat.id) || [],
      feature_order: novel.feature_order ?? 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (novel) => {
    if (window.confirm(`确定要删除小说 "${novel.title}" 吗？`)) {
      try {
        await api.delete(`/novels/${novel.id}`);
        fetchNovels();
        alert('小说删除成功');
      } catch (error) {
        console.error('删除小说失败:', error);
        alert('删除小说失败');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.author.trim()) {
      alert('请填写小说标题和作者');
      return;
    }

    try {
      const trimmedPrompt = formData.translation_prompt?.trim() || '';
      const submitData = {
        ...formData,
        translation_prompt: trimmedPrompt ? trimmedPrompt : null,
        category_ids: formData.categories,
        upload_by: formData.upload_by ? parseInt(formData.upload_by) : null,
        feature_order: Number.isFinite(Number(formData.feature_order))
          ? Number(formData.feature_order)
          : 0,
        is_latest: Number.isFinite(Number(formData.is_latest))
          ? Number(formData.is_latest)
          : 0,
        is_tuijian: Number.isFinite(Number(formData.is_tuijian))
          ? Number(formData.is_tuijian)
          : 0,
      };
      delete submitData.categories;

      if (editingNovel) {
        await api.put(`/novels/${editingNovel.id}`, submitData);
        alert('小说更新成功');
      } else {
        await api.post('/novels/', submitData);
        alert('小说创建成功');
      }
      
      setShowModal(false);
      fetchNovels();
    } catch (error) {
      console.error('保存小说失败:', error);
      alert('保存小说失败');
    }
  };

  const handleCategoryChange = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(categoryId)
        ? prev.categories.filter(id => id !== categoryId)
        : [...prev.categories, categoryId]
    }));
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px' },
    { key: 'title', label: '小说标题' },
    { key: 'author', label: '作者', width: '120px' },
    {
      key: 'feature_order',
      label: '排序值',
      width: '100px',
    },
    { 
      key: 'categories', 
      label: '分类', 
      render: (categories) => (
        <div className={styles.categories}>
          {categories?.map(cat => (
            <span key={cat.id} className={styles.categoryTag}>
              {cat.name}
            </span>
          )) || '无'}
        </div>
      )
    },
    {
      key: 'translation_prompt',
      label: '翻译提示',
      render: (value) => (
        value ? (
          <div className={styles.promptCell}>
            {value}
          </div>
        ) : '—'
      )
    },
    {
      key: 'is_latest',
      label: '最新',
      width: '80px',
      render: (value) => (
        <span className={styles.numericBadge}>{Number(value) || 0}</span>
      ),
    },
    {
      key: 'is_tuijian',
      label: '推荐',
      width: '80px',
      render: (value) => (
        <span className={styles.numericBadge}>{Number(value) || 0}</span>
      ),
    },
    { 
      key: 'is_completed', 
      label: '状态', 
      width: '100px',
      render: (value) => (
        <span className={`${styles.status} ${value ? styles.completed : styles.ongoing}`}>
          {value ? '已完结' : '连载中'}
        </span>
      )
    },
    { 
      key: 'volumes_count', 
      label: '卷数', 
      width: '80px',
      render: (value, row) => row.volumes?.length || 0
    }
  ];

  return (
    <div className={styles.novelManagement}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>小说管理</h1>
          <p className={styles.subtitle}>管理小说作品信息</p>
        </div>
        <button className={styles.createBtn} onClick={handleCreate}>
          <PlusIcon size={18} />
          添加小说
        </button>
      </div>

      <div className={styles.stats}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <BookIcon size={24} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>{novels.length}</div>
            <div className={styles.statLabel}>总小说数</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <CheckIcon size={22} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {novels.filter(novel => novel.is_completed).length}
            </div>
            <div className={styles.statLabel}>已完结</div>
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <EditIcon size={22} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statNumber}>
              {novels.filter(novel => !novel.is_completed).length}
            </div>
            <div className={styles.statLabel}>连载中</div>
          </div>
        </div>
      </div>

      <AdminTable
        data={novels}
        columns={columns}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyMessage="暂无小说数据"
      />

      {showModal && (
        <AdminModal
          title={editingNovel ? '编辑小说' : '添加小说'}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
          size="large"
        >
          <div className={styles.form}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>小说标题 *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="请输入小说标题"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>作者 *</label>
                <input
                  type="text"
                  className={styles.input}
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  placeholder="请输入作者姓名"
                  required
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>简介</label>
              <textarea
            className={styles.textarea}
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="请输入小说简介"
            rows="4"
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>翻译提示词</label>
          <textarea
            className={styles.textarea}
            value={formData.translation_prompt}
            onChange={(e) => setFormData({ ...formData, translation_prompt: e.target.value })}
            placeholder="记录翻译时的注意事项，例如称谓、语气或专有名词的处理方式"
            rows="4"
          />
          <p className={styles.promptHint}>
            示例：古代背景角色自称“儿”时翻译为 I 或 your son。
          </p>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>原始链接</label>
            <input
                  type="url"
                  className={styles.input}
                  value={formData.book_url}
                  onChange={(e) => setFormData({ ...formData, book_url: e.target.value })}
                  placeholder="https://example.com/book/123"
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>封面图片URL</label>
                <input
                  type="url"
                  className={styles.input}
                  value={formData.cover_image_url}
                  onChange={(e) => setFormData({ ...formData, cover_image_url: e.target.value })}
                  placeholder="/static/images/covers/default_cover.jpg"
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
            <label className={styles.label}>上传者</label>
            <select
              className={styles.select}
              value={formData.upload_by}
              onChange={(e) => setFormData({ ...formData, upload_by: e.target.value })}
            >
              <option value="">请选择上传者</option>
              {adminUsers.map(user => (
                <option key={user.id} value={user.id}>
                  {user.username}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={formData.is_completed}
                onChange={(e) => setFormData({ ...formData, is_completed: e.target.checked })}
              />
              <span className={styles.checkboxText}>已完结</span>
            </label>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.label}>最新标记</label>
            <input
              type="number"
              className={styles.input}
              value={formData.is_latest}
              onChange={(e) => setFormData({ ...formData, is_latest: e.target.value })}
              placeholder="请输入整数，0 表示否"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.label}>推荐标记</label>
            <input
              type="number"
              className={styles.input}
              value={formData.is_tuijian}
              onChange={(e) => setFormData({ ...formData, is_tuijian: e.target.value })}
              placeholder="请输入整数，0 表示否"
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>分类</label>
          <div className={styles.categoryGrid}>
                {categories.map(category => (
                  <label key={category.id} className={styles.categoryLabel}>
                    <input
                      type="checkbox"
                      checked={formData.categories.includes(category.id)}
                      onChange={() => handleCategoryChange(category.id)}
                    />
                    <span className={styles.categoryText}>{category.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className={styles.formGroup}>
              <label className={styles.label}>推荐排序值</label>
              <input
                type="number"
                className={styles.input}
                value={formData.feature_order}
                onChange={(e) => setFormData({ ...formData, feature_order: e.target.value })}
                placeholder="数值越小排序越靠前（默认 0）"
                min={0}
              />
              <p className={styles.promptHint}>
                用于控制前台推荐或展示顺序，保持为整数，越小越靠前
              </p>
            </div>
          </div>
        </AdminModal>
      )}
    </div>
  );
}

export default NovelManagement;
